import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import AuthContext from 'contexts/AuthContext';
import { MODE_STORAGE_KEY, MODE_FORCED_EVENT, readStoredMode, writeStoredMode } from 'utils/mode';

// ==============================|| CONTEXTO - MODO PRODUÇÃO / SANDBOX ||============================== //

const ModeContext = createContext(null);

export const ModeProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const [mode, setModeState] = useState(() => readStoredMode());

  const canUseLive = Boolean(user && (user.can_use_live ?? user.verification_status === 'verified'));

  // Ajusta o modo quando o perfil do comerciante é carregado ou muda
  useEffect(() => {
    if (!user) return;
    if (!canUseLive && mode !== 'test') {
      writeStoredMode('test');
      setModeState('test');
    } else if (canUseLive && !mode) {
      writeStoredMode('live');
      setModeState('live');
    }
  }, [user, canUseLive, mode]);

  // O backend devolveu 403 (conta não validada) e o axios passou para Sandbox
  useEffect(() => {
    const onForced = () => setModeState('test');
    const onStorage = (event) => {
      if (event.key === MODE_STORAGE_KEY) setModeState(readStoredMode());
    };
    window.addEventListener(MODE_FORCED_EVENT, onForced);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(MODE_FORCED_EVENT, onForced);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const setMode = useCallback(
    (newMode) => {
      if (newMode !== 'live' && newMode !== 'test') return false;
      if (newMode === 'live' && !canUseLive) return false;
      writeStoredMode(newMode);
      setModeState(newMode);
      return true;
    },
    [canUseLive]
  );

  // Até o perfil carregar, assume o modo guardado (ou Produção, que é o padrão do backend)
  const effectiveMode = mode || 'live';

  const value = useMemo(
    () => ({ mode: effectiveMode, isLive: effectiveMode === 'live', isSandbox: effectiveMode === 'test', canUseLive, setMode }),
    [effectiveMode, canUseLive, setMode]
  );

  return <ModeContext.Provider value={value}>{children}</ModeContext.Provider>;
};

export const useMode = () => {
  const context = useContext(ModeContext);
  if (!context) throw new Error('useMode deve ser usado dentro de <ModeProvider>');
  return context;
};

export default ModeContext;
